
import { hasBeenReadByCurrentUser } from "../utils/messageUtils";
/**
 * Filters messages based on local flags (starred, snoozed, unread), priority, and payload content.
 *
 * @param {Array} messages - Array of Communication resources
 * @param {Object} options
 * @param {string} options.flag - "starred" | "snoozed" | "unread"
 * @param {Object} options.priorityFilter - e.g., { urgent: true, routine: true }
 * @param {string} options.practitionerId - Practitioner ID for flag matching
 * @param {string} [options.payloadQuery] - Optional search string to match payload content
 * @returns {Array} - Filtered message array
 */
export const filterLocalMessages = (
  messages = [],
  { flag = "none", practitionerId, payloadQuery }
) => {
  const safeQuery = typeof payloadQuery === "string" ? payloadQuery.trim() : "";

  let filtered = messages.filter((comm) => {
    const reasons = [];
    const isSnoozed = hasStatusFlag(comm, practitionerId, "snoozed");

    // ✅ If snoozed is selected, only include snoozed messages
    if (flag === "snoozed") {
      if (!isSnoozed) {
        reasons.push("not snoozed (snoozed view)");
      }
    } else {
      // ✅ Otherwise, always exclude snoozed messages
      if (isSnoozed) {
        reasons.push("snoozed message excluded");
      }

      // ✅ Apply other flags
      if (flag === "starred" && !hasStatusFlag(comm, practitionerId, "starred")) {
        reasons.push("not starred");
      }
      if (flag === "unread" && hasBeenReadByCurrentUser(comm, practitionerId)) {
        reasons.push("already read");
      }
    }

    if (reasons.length > 0) {
      console.log(`❌ Message ${comm.id} excluded:`, reasons);
      return false;
    }

    return true;
  });

  // ✅ Payload query filtering
  if (safeQuery) {
    console.log("🔍 Applying payload query filter:", safeQuery);
    const beforePayload = filtered.length;
    filtered = filterMessagesByPayloadContent(filtered, safeQuery);
    console.log(`🧹 Payload filter reduced messages from ${beforePayload} to ${filtered.length}`);
  }

  return filtered;
};
/**
 * Filters messages by payload content: contentString or attachment title
 *
 * @param {Array} messages - Array of Communication resources
 * @param {string} query - Case-insensitive search string
 * @returns {Array} - Filtered message array matching the payload text
 */
/**
 * Filters messages by payload content — contentString or attachment title.
 * Only applies filter when query has a non-empty, trimmed value.
 *
 * @param {Array} messages - Array of Communication resources
 * @param {string} query - Case-insensitive search string
 * @returns {Array} - Filtered message array, or original array if no query
 */
export const filterMessagesByPayloadContent = (messages = [], query = "") => {
  const safeQuery = typeof query === "string" ? query.trim().toLowerCase() : "";

  // Skip filtering if query is empty, zero-length, or not a string
  if (!safeQuery) return messages; // ✅ Only filter if there's actual input

  return messages.filter((comm) =>
    comm.payload?.some(
      (item) =>
        item.contentString?.toLowerCase().includes(safeQuery) ||
        item.contentAttachment?.title?.toLowerCase().includes(safeQuery)
    )
  );
};

// Helper to check for status flags (e.g. starred, snoozed)
const hasStatusFlag = (comm, practitionerId, code) => {
  return comm.extension?.some((ext) => {
    if (ext.url !== "http://endpointhealth.ca/fhir/StructureDefinition/statusFlag") return false;
    const userRef = ext.extension?.find((e) => e.url === "user")?.valueReference?.reference;
    const flagCode = ext.extension?.find((e) => e.url === "flag")?.valueCode;
    const normalizedRef = userRef?.split("/")?.pop();
    return normalizedRef === practitionerId && flagCode === code;
  });
};